import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { MapPin, Calendar, Building2, ChevronRight, Users, Clock, Utensils, Compass, Train } from 'lucide-react';
import { translateCountryCode, translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';
import YouTubeAutoTranslate from '@/components/worldcup/YouTubeAutoTranslate';

const API_BASE = '';

export default function WCCityPage() {
  const { country, citySlug } = useParams();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const isTR = currentLang === 'tr';

  useEffect(() => {
    if (!citySlug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/worldcup/city/${citySlug}?lang=${currentLang}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [citySlug, currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.city) return <div className="container mx-auto px-4 py-8 text-slate-400">Şehir bulunamadı</div>;

  const { city, stadiums, fixtures } = data;
  const matchPath = isTR ? 'mac' : 'match';
  const countryNames = { usa: isTR ? 'ABD' : 'USA', canada: isTR ? 'Kanada' : 'Canada', mexico: isTR ? 'Meksika' : 'Mexico' };

  return (
    <div data-testid="wc-city-page">
      <WCSubmenu />
      <SEOHead title={`${city.name} - ${isTR ? 'Dünya Kupası 2026' : 'World Cup 2026'} | ODDSIS`}
        description={isTR ? city.description_tr : city.description_en}
        canonical={data.seo?.canonical} jsonLd={data.seo?.jsonld} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 70%, #020617 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-6xl py-10 md:py-14">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{isTR ? 'Ana Sayfa' : 'Home'}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">WC 2026</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${city.country_slug === "usa" && isTR ? "abd" : city.country_slug === "canada" && isTR ? "kanada" : city.country_slug === "mexico" && isTR ? "meksika" : city.country_slug}`} className="hover:text-amber-300 text-amber-400/70">{countryNames[city.country_slug]}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{city.name}</span>
          </div>
          <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.2em] mb-2">{isTR ? 'EV SAHİBİ ŞEHİR' : 'HOST CITY'} - {translateCountryCode(city.country, currentLang)}</p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{city.name}</h1>
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{city.population}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{city.state}, {city.country}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{city.timezone}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl py-8 space-y-8">
        {/* Video - Top of page */}
        {city.video_id && (
          <section>
            <YouTubeAutoTranslate videoId={city.video_id} title={`${city.name} - ${isTR ? 'Dünya Kupası 2026' : 'World Cup 2026'}`} />
          </section>
        )}

        {/* Description */}
        <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <p className="text-slate-300 leading-relaxed">{isTR ? city.description_tr : city.description_en}</p>
        </section>

        {/* Stadiums */}
        <section>
          <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Bu Şehirdeki Stadyumlar' : 'Stadiums in This City'}
          </h2>
          <div className="space-y-3">
            {stadiums.map(s => (
              <Link key={s.slug} to={`${wcBase}/${isTR ? "stadyum" : "stadium"}/${s.slug}`} data-testid={`stadium-${s.slug}`}
                className="group flex items-center gap-4 bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-amber-500/40 transition-all">
                <div className="flex-1">
                  <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name}</h3>
                  <p className="text-xs text-slate-500">{(s.capacity || 0).toLocaleString()} {isTR ? 'kişilik' : 'capacity'} - {s.opened}</p>
                </div>
                <span className="text-xs text-amber-400 font-medium">{s.wc_matches} {isTR ? 'maç' : 'matches'}</span>
                {s.hosts_final && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-bold">FİNAL</span>}
                <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400" />
              </Link>
            ))}
          </div>
        </section>

        {/* Fixtures */}
        {fixtures?.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Calendar className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Bu Şehirdeki Maçlar' : 'Matches in This City'} ({fixtures.length})
            </h2>
            <div className="space-y-1.5">
              {fixtures.map(m => {
                const date = m.date ? new Date(m.date) : null;
                return (
                  <Link key={m.id} to={`${wcBase}/${matchPath}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-800/50 bg-slate-900/30 hover:bg-slate-800/50 hover:border-amber-500/20 transition-all">
                    <span className="text-xs text-slate-500 w-20 shrink-0">{date?.toLocaleDateString(isTR ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short' })}</span>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className="text-sm font-semibold text-slate-200 text-right">{translateCountryName(m.home_name, currentLang)}</span>
                      <img src={m.home_logo} alt="" className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-slate-600 px-2">vs</span>
                    <div className="flex items-center gap-2 flex-1">
                      <img src={m.away_logo} alt="" className="w-5 h-5" />
                      <span className="text-sm font-semibold text-slate-200">{translateCountryName(m.away_name, currentLang)}</span>
                    </div>
                    <span className="text-[10px] text-amber-400/60 w-24 text-right">{isTR ? m.round.replace('Group Stage - ','Grup - ') : m.round}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Grid: Attractions, Food, Transport */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Compass className="w-4 h-4 text-amber-400" />{isTR ? 'Gezilecek Yerler' : 'Attractions'}</h3>
            <ul className="space-y-1.5">
              {(city.attractions_tr || []).map((a, i) => (
                <li key={i} className="text-xs text-slate-400 flex items-center gap-2"><span className="w-1 h-1 bg-amber-400 rounded-full" />{a}</li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Utensils className="w-4 h-4 text-amber-400" />{isTR ? 'Yemek & Gece Hayatı' : 'Food & Nightlife'}</h3>
            <ul className="space-y-1.5">
              {(city.food_tr || []).map((f, i) => (
                <li key={i} className="text-xs text-slate-400 flex items-center gap-2"><span className="w-1 h-1 bg-emerald-400 rounded-full" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Train className="w-4 h-4 text-amber-400" />{isTR ? 'Ulaşım Rehberi' : 'Transport Guide'}</h3>
            <ul className="space-y-1.5">
              {(city.transport_tr || []).map((t, i) => (
                <li key={i} className="text-xs text-slate-400 flex items-center gap-2"><span className="w-1 h-1 bg-sky-400 rounded-full" />{t}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Internal Links */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800">
          <Link to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${city.country_slug === "usa" && isTR ? "abd" : city.country_slug === "canada" && isTR ? "kanada" : city.country_slug === "mexico" && isTR ? "meksika" : city.country_slug}`} className="text-xs text-amber-400/70 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">{countryNames[city.country_slug]} {isTR ? 'Tüm Şehirler' : 'All Cities'}</Link>
          {stadiums.map(s => (
            <Link key={s.slug} to={`${wcBase}/${isTR ? "stadyum" : "stadium"}/${s.slug}`} className="text-xs text-slate-400 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">{s.name}</Link>
          ))}
          <Link to={`${wcBase}/${isTR ? 'skor-ve-fikstur' : 'scores'}`} className="text-xs text-slate-400 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">{isTR ? 'Tüm Fikstür' : 'All Fixtures'}</Link>
        </div>
      </div>
    </div>
  );
}
